import { Body, Button, Divider, Flex, Image } from '@hover/blueprint';
import { iArrowLeft } from '@hover/icons';
import React, { useMemo, useState } from 'react';

import { CardHeader } from 'src/modules/designStudio/components/DesignMenu/CardHeader';
import { CircleButton } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/CircleButton';
import {
  TestIds,
  TestPageIds,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import type { VariationsColorGroup } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails/utils';
import {
  createSizeOptions,
  extractVariationColorGroupsFromProduct,
} from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails/utils';
import { VariationColoredTexture } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/VariationColoredTexture';
import { useTrackProductSelectionEvent } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useTrackProductSelectionEvent';
import { ScrollMenu } from 'src/modules/designStudio/components/DesignMenu/ScrollMenu';
import { ProductTypeNames } from 'src/modules/designStudio/helpers/productSelection/constants';
import { ProviderLogosById } from 'src/modules/designStudio/helpers/productSelection/logos';
import type {
  ProductCategory,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useLinkOpener } from 'src/shared/hooks/useLinkOpener';

const VariationDetailAttribute = ({
  name,
  value,
}: {
  name: string;
  value: string;
}) => (
  <Flex direction="row">
    <Body color="neutral.500" fontSize={100} fontWeight={700} my={0}>
      {name}
    </Body>
    <Body fontSize="14px" fontWeight={400} marginLeft="auto" my={0}>
      {value}
    </Body>
  </Flex>
);

export type VariationDetailsParams = {
  variation: Variation;
  category: ProductCategory;
  onBack: () => void;
  onDifferentSizeSelected?: (variation: Variation) => void;
  onDifferentColorSelected?: (variation: Variation) => void;
  onApply?: () => void;
};

export const VariationDetails = ({
  variation,
  onBack,
  onDifferentSizeSelected,
  onDifferentColorSelected,
  onApply,
  category,
}: VariationDetailsParams) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const linkOpener = useLinkOpener();

  const [viewAll, setViewAll] = useState(false);

  const hasURL =
    variation.manufacturerURL !== undefined &&
    variation.manufacturerURL.trim() !== '';

  const { trackRealProductSelectionEvent } = useTrackProductSelectionEvent({
    category,
  });

  const onOrderSample = () => {
    trackRealProductSelectionEvent(
      {
        track_id: `product-selection-${category}-order-sample-click`,
      },
      variation,
    );
    if (hasURL) {
      linkOpener(variation.manufacturerURL!);
    }
  };

  const variationHasColor: boolean = useMemo(
    () => !!variation.color,
    [variation],
  );

  const colorGroups: VariationsColorGroup[] = useMemo(
    () =>
      variationHasColor
        ? extractVariationColorGroupsFromProduct(variation.product)
        : [],
    [variationHasColor, variation],
  );

  const sizes = useMemo(() => {
    if (variationHasColor) {
      /**
       * If the variation has a color, we only show sizes of variations with same color
       */
      const index = colorGroups.findIndex(
        group => group.hex === variation.color!.hex,
      );
      return createSizeOptions(colorGroups[index]);
    }
    return [];
  }, [variationHasColor, colorGroups, variation]);

  const colorCircleButtonsArray = useMemo(
    () => (viewAll ? colorGroups : colorGroups.slice(0, 6)),
    [viewAll, colorGroups],
  );

  const toggleViewAll = () => {
    trackRealProductSelectionEvent(
      {
        track_id: viewAll
          ? `product-selection-${category}-view-all-click`
          : `product-selection-${category}-hide-options-click`,
      },
      variation,
    );
    setViewAll(!viewAll);
  };

  const providerLogo = ProviderLogosById[variation.product.provider.uuid];

  return (
    <ScrollMenu.Menu data-testid={TestPageIds.VARIATION_DETAILS}>
      <ScrollMenu.Header>
        <CardHeader
          headerText=""
          leftActionHandler={onBack}
          leftIcon={iArrowLeft}
          leftIconId={TestIds.BACK_BUTTON}
          m={0}
        />
      </ScrollMenu.Header>
      <ScrollMenu.Body>
        <Flex direction="column" gap="16px">
          <VariationColoredTexture
            borderRadius={350}
            color={variation.color?.hex ?? undefined}
            disableHover
            height="190px"
            isSelected={false}
            thumbnail={variation.thumbnailURL ?? ''}
            width="100%"
          />
          <Flex direction="column" gap="8px">
            {providerLogo && (
              <Image
                alt="brandLogo"
                color="black"
                maxWidth="50%"
                src={providerLogo}
              />
            )}
            <Body fontSize={600} fontWeight={700} my={0}>
              {variation.name}
            </Body>
          </Flex>
          <Flex direction="column">
            {colorGroups.length !== 0 && onDifferentColorSelected && (
              <>
                <Flex alignItems="center" direction="row">
                  <Body fontSize={300} fontWeight={700} my={0}>
                    {t('productCatalogMenu.productDetails.colors')}
                  </Body>
                  <Button
                    marginLeft="auto"
                    onClick={toggleViewAll}
                    size="small"
                    variant="ghost"
                  >
                    <Body fontSize={300} fontWeight={400} my={0}>
                      {!viewAll
                        ? t('productCatalogMenu.productDetails.viewAll')
                        : t('productCatalogMenu.productDetails.hide')}
                    </Body>
                  </Button>
                </Flex>
                <Flex
                  direction="row"
                  flexWrap="wrap"
                  gap={200}
                  justifyContent="flex-start"
                >
                  {colorCircleButtonsArray.map(group => (
                    <CircleButton
                      buttonSize="35px"
                      color={group.hex}
                      dataTestId={group.hex}
                      isSelected={variation.color?.hex === group.hex}
                      key={group.hex}
                      label="ColorOption"
                      onClick={() => {
                        onDifferentColorSelected(group.variations[0]);
                      }}
                      outlineOffset="2px"
                    />
                  ))}
                </Flex>
              </>
            )}
            {sizes.length !== 0 && onDifferentSizeSelected && (
              <>
                <Divider />
                <Flex direction="column">
                  <Body fontSize={300} fontWeight={700} my={0}>
                    {t('productCatalogMenu.productDetails.size')}
                  </Body>
                  <Flex direction="row" gap={200} overflowX="auto" py={300}>
                    {sizes.map(group => {
                      const isActive = group.size === variation.facets.sizing;
                      return (
                        <Button
                          height={350}
                          isActive={isActive}
                          onClick={() => {
                            if (!isActive) {
                              onDifferentSizeSelected(group.variations[0]);
                            }
                          }}
                          variant={isActive ? 'primary' : 'glass'}
                        >
                          {group.size.split(' ')[0]}
                        </Button>
                      );
                    })}
                  </Flex>
                </Flex>
              </>
            )}
            <Divider />
            <Flex direction="column" gap={300}>
              <Body fontSize={300} fontWeight={700} my={0}>
                {t('productCatalogMenu.productDetails.details')}
              </Body>

              {variation.facets.sizing && (
                <VariationDetailAttribute
                  name={t('productCatalogMenu.productDetails.size')}
                  value={variation.facets.sizing}
                />
              )}

              <VariationDetailAttribute
                name={t('productCatalogMenu.productDetails.color')}
                value={variation.name}
              />

              <VariationDetailAttribute
                name={t('productCatalogMenu.productDetails.type')}
                value={ProductTypeNames[variation.product.type]}
              />
            </Flex>
          </Flex>
        </Flex>
      </ScrollMenu.Body>
      <ScrollMenu.Footer>
        <Flex direction="row" gap={300}>
          {hasURL && (
            <Button
              flexBasis={0}
              flexGrow={1}
              height={400}
              onClick={onOrderSample}
              variant="secondary"
            >
              {t('productCatalogMenu.productDetails.order')}
            </Button>
          )}
          {onApply && (
            <Button flexBasis={0} flexGrow={1} height={400} onClick={onApply}>
              {t('productCatalogMenu.productDetails.apply')}
            </Button>
          )}
        </Flex>
      </ScrollMenu.Footer>
    </ScrollMenu.Menu>
  );
};
